import { getBasePath } from '@ais-ui/api';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useSession } from '@ais-ui/websockets';
import {
  UseMutationResult,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { settingsFetch, ServiceUnavailableError } from './resilience';
import type { SettingsDomain } from './types';

export const buildUserSettingsRequest = (args: {
  hostname: string;
  basePath: string;
  sessionId: string;
  authHeader: string;
}): {
  url: (domain: SettingsDomain | '_import') => string;
  collectionUrl: (
    domain: SettingsDomain,
    collectionPath: string,
    itemId?: string,
  ) => string;
  headers: HeadersInit;
} => {
  const isLocalhost = args.hostname.includes('localhost');
  // Mirror the base-URL convention every other service hook uses
  // (useGroundingApi / useGPSLostApi / useS2SApi): on localhost the ingress
  // prefix is `api/ingress/<service>` with Basic auth; when deployed it is
  // `aisweb/api/ingress/<service>` with the Aisweb session header. `basePath`
  // already resolves to VITE_LOCALHOST_BACKEND_HOSTNAME (or the current host)
  // with a trailing slash, so both cases stay same-origin and CORS-safe. The
  // deployed service serves under FASTAPI_ROOT_PATH=/api/ingress/user-settings.
  const root = isLocalhost
    ? `${args.basePath}api/ingress/user-settings`
    : `${args.basePath}aisweb/api/ingress/user-settings`;
  const headers: Record<string, string> = {
    'X-Application-Name': 'marinepulse',
    'Content-Type': 'application/json',
  };
  if (isLocalhost) headers.Authorization = args.authHeader;
  else headers['Aisweb-Session'] = args.sessionId;
  return {
    url: (domain) => `${root}/settings/${domain}`,
    collectionUrl: (domain, collectionPath, itemId) => {
      const base = `${root}/settings/${domain}/${collectionPath}`;
      return itemId ? `${base}/${itemId}` : base;
    },
    headers,
  };
};

const useRequest = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  return buildUserSettingsRequest({
    hostname,
    basePath: getBasePath(),
    sessionId: sessionId ?? '',
    authHeader: getAuthHeaderFromCredentials(
      credentials?.username || '',
      credentials?.password || '',
    ),
  });
};

export const useUserSettingsQuery = <T>(
  domain: SettingsDomain,
  opts?: { enabled?: boolean },
): UseQueryResult<T> => {
  const req = useRequest();
  return useQuery(
    {
      queryKey: ['userSettings', domain],
      enabled: opts?.enabled ?? true,
      // The service is a fallback source, never a hard dependency: retry once,
      // keep values fresh briefly, and don't refetch on focus. A failure just
      // leaves the engine on its blob-derived value.
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      queryFn: async () => {
        const res = await settingsFetch(req.url(domain), {
          method: 'GET',
          headers: req.headers,
        });
        if (!res.ok)
          throw new ServiceUnavailableError(
            `GET ${domain} settings failed: ${res.status}`,
          );
        return (await res.json()) as T;
      },
    },
    `userSettings:${domain}:get`,
  );
};

export const useUserSettingsPatch = <T>(
  domain: SettingsDomain,
): UseMutationResult<T, Error, Partial<T>> => {
  const req = useRequest();
  const queryClient = useQueryClient();
  return useMutation(
    {
      mutationFn: async (patch: Partial<T>) => {
        const res = await settingsFetch(req.url(domain), {
          method: 'PATCH',
          headers: req.headers,
          body: JSON.stringify(patch),
        });
        if (!res.ok)
          throw new ServiceUnavailableError(
            `PATCH ${domain} settings failed: ${res.status}`,
          );
        return (await res.json()) as T;
      },
      onSuccess: (data) =>
        queryClient.setQueryData(['userSettings', domain], data),
    },
    `userSettings:${domain}:patch`,
  );
};

/** Server-authoritative login-import idempotency flag (`_import` meta-domain). */
export interface ImportStatus {
  imported: boolean;
}

/**
 * Reads the reserved `_import` meta-domain. Server GET auto-creates
 * `{ imported: false }`. Used by the login orchestrator to decide whether the
 * one-time seed of all owned domains still needs to run.
 */
export const useImportStatusQuery = (opts?: {
  enabled?: boolean;
}): UseQueryResult<ImportStatus> => {
  const req = useRequest();
  return useQuery(
    {
      queryKey: ['userSettings', '_import'],
      enabled: opts?.enabled ?? true,
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      queryFn: async () => {
        const res = await settingsFetch(req.url('_import'), {
          method: 'GET',
          headers: req.headers,
        });
        if (!res.ok)
          throw new ServiceUnavailableError(
            `GET _import status failed: ${res.status}`,
          );
        return (await res.json()) as ImportStatus;
      },
    },
    `userSettings:_import:get`,
  );
};

/** Marks the one-time login import complete (`PATCH /settings/_import`). */
export const useSetImported = (): UseMutationResult<
  ImportStatus,
  Error,
  boolean
> => {
  const req = useRequest();
  const queryClient = useQueryClient();
  return useMutation(
    {
      mutationFn: async (imported: boolean) => {
        const res = await settingsFetch(req.url('_import'), {
          method: 'PATCH',
          headers: req.headers,
          body: JSON.stringify({ imported }),
        });
        if (!res.ok)
          throw new ServiceUnavailableError(
            `PATCH _import status failed: ${res.status}`,
          );
        return (await res.json()) as ImportStatus;
      },
      onSuccess: (data) =>
        queryClient.setQueryData(['userSettings', '_import'], data),
    },
    `userSettings:_import:patch`,
  );
};
export interface CollectionPage<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * Fetches a collection domain's list endpoint. The list may return trimmed
 * summaries (see each domain's backend `summary_model`); callers that need full
 * items should treat the result as read-only display data or fetch items by id.
 * Like the document query, this is a soft fallback source: retry once, no
 * refetch-on-focus, and a failure just leaves the engine on its blob value.
 */
export const useUserSettingsCollectionQuery = <TSummary>(
  domain: SettingsDomain,
  collectionPath: string,
  opts?: { enabled?: boolean },
): UseQueryResult<TSummary[]> => {
  const req = useRequest();
  return useQuery(
    {
      // `collectionPath` is part of the endpoint's identity, not just
      // `domain` — include it so two collections on the same domain (should
      // one ever exist) can't collide on the same cache entry.
      queryKey: ['userSettings', domain, 'collection', collectionPath],
      enabled: opts?.enabled ?? true,
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      queryFn: async () => {
        const res = await settingsFetch(
          req.collectionUrl(domain, collectionPath),
          { method: 'GET', headers: req.headers },
        );
        if (!res.ok)
          throw new ServiceUnavailableError(
            `GET ${domain} collection failed: ${res.status}`,
          );
        const page = (await res.json()) as CollectionPage<TSummary>;
        return page.items;
      },
    },
    `userSettings:${domain}:list`,
  );
};

/**
 * Optimistically seeds the service read cache for a document domain from the
 * authoritative blob write, so phase-2 reads reflect the user's change even if
 * the best-effort service PATCH is dropped. The next natural query lifecycle
 * reconciles against the server.
 */
export const useSeedServiceCache = (): ((
  domain: SettingsDomain,
  value: unknown,
) => void) => {
  const queryClient = useQueryClient();
  return (domain, value) =>
    queryClient.setQueryData(['userSettings', domain], value);
};

/**
 * Optimistically seeds a collection domain's list cache from the authoritative
 * blob-derived next array, so phase-2 reads reflect the change even if a
 * best-effort per-item op is dropped.
 */
export const useSeedServiceCollectionCache = (): ((
  domain: SettingsDomain,
  collectionPath: string,
  items: unknown[],
) => void) => {
  const queryClient = useQueryClient();
  return (domain, collectionPath, items) =>
    queryClient.setQueryData(
      ['userSettings', domain, 'collection', collectionPath],
      items,
    );
};

export interface CollectionMutations<TItem extends { id: string }> {
  createItem: (item: TItem) => Promise<TItem>;
  patchItem: (id: string, patch: Partial<TItem>) => Promise<TItem>;
  deleteItem: (id: string) => Promise<void>;
}

/**
 * Per-item CRUD for a collection domain, each routed through the timeout client
 * so a slow/absent test backend surfaces a `ServiceUnavailableError` the engine
 * can swallow. We send the item's `id` on create: the backend upserts by id
 * (`item.get("id") or uuid4()`), which keeps blob and service ids aligned so
 * later per-item PATCH/DELETE target the same row, and makes seeding idempotent.
 */
export const useUserSettingsCollectionMutations = <
  TItem extends { id: string },
>(
  domain: SettingsDomain,
  collectionPath: string,
): CollectionMutations<TItem> => {
  const req = useRequest();

  const createItem = async (item: TItem): Promise<TItem> => {
    const res = await settingsFetch(req.collectionUrl(domain, collectionPath), {
      method: 'POST',
      headers: req.headers,
      body: JSON.stringify(item),
    });
    if (!res.ok)
      throw new ServiceUnavailableError(
        `POST ${domain} item failed: ${res.status}`,
      );
    return (await res.json()) as TItem;
  };

  const patchItem = async (
    id: string,
    patch: Partial<TItem>,
  ): Promise<TItem> => {
    const res = await settingsFetch(
      req.collectionUrl(domain, collectionPath, id),
      { method: 'PATCH', headers: req.headers, body: JSON.stringify(patch) },
    );
    if (!res.ok)
      throw new ServiceUnavailableError(
        `PATCH ${domain} item failed: ${res.status}`,
      );
    return (await res.json()) as TItem;
  };

  const deleteItem = async (id: string): Promise<void> => {
    const res = await settingsFetch(
      req.collectionUrl(domain, collectionPath, id),
      { method: 'DELETE', headers: req.headers },
    );
    if (!res.ok && res.status !== 404)
      throw new ServiceUnavailableError(
        `DELETE ${domain} item failed: ${res.status}`,
      );
  };

  return { createItem, patchItem, deleteItem };
};
